import { useCallback, useEffect } from "react";

import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";

/** The user-scoped settings key: whether My Work paints its greeting line.
 *  One switch per viewer, following them across workspaces and devices. */
const MY_WORK_GREETING_KEY = "my-work-greeting";

/** The last answer the server gave, kept in this browser so the first paint
 *  can honor it before the setting query returns. */
const MIRROR_KEY = "my-work-greeting-mirror";
const MIRROR_DEFAULTS = { visible: true };

/**
 * The stored payload is loose JSON, and anything but an explicit `false`
 * shows the greeting — the shipped default is ON, so a never-written key
 * reads as visible. Module-level so `useUserSetting` can memoize on it.
 */
export function parseGreetingVisible(value: unknown): boolean {
  return value !== false;
}

/**
 * Whether the viewer wants My Work's greeting line ("Good morning, …") at
 * all. A per-viewer display option: the page keeps its pinned title header
 * either way, so hiding the line only removes the h1 that scrolls with the
 * board, and the board opens on the same top inset it always had.
 *
 * Two layers. The server setting is the truth once known. Until the query
 * answers — a fresh reload landing on My Work — the value is the
 * localStorage mirror of the LAST answer, so a reader who hid the greeting
 * never sees it flash in and collapse, shifting the whole board up a line.
 * The mirror re-warms whenever the server disagrees (toggled elsewhere).
 */
export function useMyWorkGreetingVisible(): {
  visible: boolean;
  setVisible: (visible: boolean) => void;
} {
  const { value, setValue, initialLoading } = useUserSetting(
    MY_WORK_GREETING_KEY,
    parseGreetingVisible,
  );
  const { state: mirror, update: updateMirror } = usePersistedState(
    MIRROR_KEY,
    MIRROR_DEFAULTS,
  );
  const visible = initialLoading ? mirror.visible : value;

  useEffect(() => {
    if (!initialLoading && mirror.visible !== value) {
      updateMirror({ visible: value });
    }
  }, [initialLoading, mirror.visible, updateMirror, value]);

  // Written to both at once: the cache write inside `setValue` flips `value`
  // on the same tick, and the mirror must not lag it by a render.
  const setVisible = useCallback(
    (next: boolean) => {
      updateMirror({ visible: next });
      setValue(next);
    },
    [setValue, updateMirror],
  );

  return { visible, setVisible };
}
